import { useMemo, useState } from 'react';
import { Alert, Text } from 'react-native';
import * as Linking from 'expo-linking';
import { type Href, router, useLocalSearchParams } from 'expo-router';

import { AuthButton, AuthLinkRow, AuthNote, AuthScreen } from '@/components/auth/auth-ui';
import { getRoleHomePath } from '@/lib/auth-routing';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/providers/auth-provider';

export default function EmailConfirmationScreen() {
  const { authFlow, isConfigured, profile, session, setAuthFlow } = useAuth();
  const params = useLocalSearchParams<{ email?: string; status?: string }>();
  const [loading, setLoading] = useState(false);
  const emailRedirectTo = useMemo(() => Linking.createURL('/email-confirmation'), []);

  const isConfirmed = params.status === 'confirmed' || authFlow === 'confirmation' || Boolean(session);

  const handleResend = async () => {
    if (!params.email) {
      Alert.alert('Email missing', 'Go back to signup and enter the email again.');
      return;
    }

    if (!isConfigured) {
      Alert.alert('Supabase not configured', 'Add your Supabase environment variables first.');
      return;
    }

    setLoading(true);

    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: params.email,
      options: {
        emailRedirectTo,
      },
    });

    setLoading(false);

    if (error) {
      Alert.alert('Resend failed', error.message);
      return;
    }

    Alert.alert('Confirmation resent', 'Check your inbox for the updated confirmation link.');
  };

  const handleContinue = () => {
    setAuthFlow('idle');
    router.replace((profile ? getRoleHomePath(profile.role) : '/sign-in') as Href);
  };

  return (
    <AuthScreen
      eyebrow={isConfirmed ? 'Email confirmed' : 'Confirm your email'}
      title={isConfirmed ? 'Your workspace is ready.' : 'Check your inbox to finish signup.'}
      subtitle={
        isConfirmed
          ? 'Your email is confirmed and your account is ready to use.'
          : 'Open the email we sent and tap the confirmation link to continue.'
      }>
      {params.email ? (
        <AuthNote>
          Confirmation email sent to <Text style={{ color: '#173924' }}>{params.email}</Text>
        </AuthNote>
      ) : null}

      {isConfirmed ? (
        <AuthButton onPress={handleContinue} title="Continue to app" />
      ) : (
        <>
          <AuthButton loading={loading} onPress={handleResend} title="Resend confirmation email" />
          <AuthLinkRow
            actionHref="/sign-in"
            actionLabel="Go to sign in"
            prompt="Already confirmed on another device?"
          />
        </>
      )}
    </AuthScreen>
  );
}
